import React, { useState, useEffect, useCallback } from 'react';
import { jobsService, sitesService, auditorsService, clientsService, settingsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Filter, 
  CalendarDays,
  LayoutGrid,
  Lock,
  Bell
} from 'lucide-react';
import RegionFilter from '../components/RegionFilter';
import { LockLegend } from '../components/JobLock';
import CalendarNotificationsDialog from '../components/CalendarNotificationsDialog';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO
} from 'date-fns';

const statusColors = {
  scheduled: 'bg-blue-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500'
};

export default function CalendarPage() {
  const { user, isAdmin, isScheduler } = useAuth();
  const { region } = useSettings();
  const { toast } = useToast();
  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [sites, setSites] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [clients, setClients] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // View mode: 'week' or 'month'
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [auditorFilter, setAuditorFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [regionId, setRegionId] = useState(null);

  const isAuditor = user?.role === 'auditor';

  // Calculate date range based on view mode
  const getDateRange = useCallback(() => {
    if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    }
  }, [viewMode, currentDate]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      
      const params = {
        status: statusFilter || undefined,
        site_id: siteFilter || undefined,
        auditor_id: auditorFilter || undefined,
        client_id: clientFilter || undefined,
        region: region || undefined,  // Filter by region (UK/UAE)
        region_id: regionId != null ? regionId : undefined,  // Filter by auditor region
        start_date: format(start, 'yyyy-MM-dd'),
        end_date: format(end, 'yyyy-MM-dd'),
        limit: 500
      };
      
      const [jobsData, sitesData, auditorsData, clientsData, settingsData, holidaysData] = await Promise.all([
        jobsService.list(params),
        sitesService.list({ is_active: true, region }).catch(() => []),
        auditorsService.list({ is_active: true }).catch(() => []),
        clientsService.list({ is_active: true, region }).catch(() => []),
        settingsService.get().catch(() => null),
        settingsService.getBankHolidays({ region }).catch(() => [])
      ]);
      
      setJobs(jobsData);
      setSites(sitesData);
      setAuditors([...auditorsData].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '') || (a.last_name || '').localeCompare(b.last_name || '')));
      setClients(clientsData);
      setSettings(settingsData);
      setBankHolidays(holidaysData);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load calendar data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, siteFilter, auditorFilter, clientFilter, regionId, getDateRange, toast, region]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const navigate = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(prev => direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1));
    } else {
      setCurrentDate(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSiteFilter('');
    setAuditorFilter('');
    setClientFilter('');
    setRegionId(null);
  };

  const hasActiveFilters = statusFilter || siteFilter || auditorFilter || clientFilter || regionId !== null;

  // Get jobs for a specific day (including multi-day jobs that span this day)
  const getJobsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return jobs.filter(job => {
      // Check for non-consecutive dates first
      if (job.job_dates && job.job_dates.length > 0) {
        return job.job_dates.includes(dayStr);
      }
      
      // Fall back to consecutive date range
      const jobStart = parseISO(job.scheduled_date);
      const jobEnd = job.end_date ? parseISO(job.end_date) : jobStart;
      
      // Check if the day falls within the job's date range (inclusive)
      const startStr = format(jobStart, 'yyyy-MM-dd');
      const endStr = format(jobEnd, 'yyyy-MM-dd');
      
      return dayStr >= startStr && dayStr <= endStr;
    });
  };

  // Get bank holiday for a specific day
  const getBankHolidayForDay = (day) => {
    if (!settings?.show_bank_holidays) return null;
    return bankHolidays.find(h => isSameDay(parseISO(h.date), day));
  };

  // iter396 — Centralised event label resolver.
  //
  // EventType today is one of:
  //   CLIENT_JOB · LEAVE_HOLIDAY · LEAVE_SICK ·
  //   LEAVE_COMPASSIONATE · LEAVE_OTHER
  // LEAVE_OTHER carries the free-text reason (meeting subject, training,
  // travel, etc.) in `description` or `notes`. Pull that into the label
  // so users see "Meeting: Q2 kickoff" instead of a generic "Leave".
  const getNonJobLabel = (job) => {
    const detail = (job.description || job.notes || '').trim();
    switch (job.event_type) {
      case 'LEAVE_HOLIDAY':       return { emoji: '🏖️', label: 'Holiday', detail };
      case 'LEAVE_SICK':          return { emoji: '🤒', label: 'Sick Leave', detail };
      case 'LEAVE_COMPASSIONATE': return { emoji: '💜', label: 'Compassionate Leave', detail };
      case 'LEAVE_OTHER':         return { emoji: '📌', label: detail || 'Other', detail: detail ? '' : '' };
      default:                    return { emoji: '📌', label: 'Event', detail };
    }
  };

  // Get color for a job (from parent client or independent client)
  const getJobColor = (job) => {
    // Leave events have specific colors
    if (job.event_type?.startsWith('LEAVE')) {
      switch (job.event_type) {
        case 'LEAVE_HOLIDAY': return '#059669'; // Green for holiday
        case 'LEAVE_SICK': return '#DC2626'; // Red for sick
        case 'LEAVE_COMPASSIONATE': return '#7C3AED'; // Purple for compassionate
        case 'LEAVE_OTHER': return '#2563EB'; // Blue for other / meeting
        default: return '#059669';
      }
    }
    // Client jobs use customer color
    if (job.site?.parent_client?.colour) return job.site.parent_client.colour;
    if (job.site?.independent_client?.colour) return job.site.independent_client.colour;
    return '#6B7280';
  };

  // Get display name for a job (handles leave events)
  const getJobDisplayName = (job) => {
    if (job.event_type?.startsWith('LEAVE')) {
      const auditorName = job.auditors?.[0] 
        ? `${job.auditors[0].first_name} ${job.auditors[0].last_name[0]}.`
        : '';
      const { emoji, label, detail } = getNonJobLabel(job);
      const suffix = detail && job.event_type !== 'LEAVE_OTHER' ? ` (${detail})` : '';
      return `${emoji} ${auditorName} - ${label}${suffix}`.trim();
    }
    return job.site?.name || 'No Site';
  };

  // Get tooltip for a job
  const getJobTooltip = (job) => {
    if (job.event_type?.startsWith('LEAVE')) {
      const auditorName = job.auditors?.[0] 
        ? `${job.auditors[0].first_name} ${job.auditors[0].last_name}`
        : 'Unknown';
      const { label, detail } = getNonJobLabel(job);
      const detailSuffix = detail && job.event_type !== 'LEAVE_OTHER' ? ` — ${detail}` : '';
      return `${auditorName} — ${label}${detailSuffix}`;
    }
    return `${job.site?.name || 'No Site'} - ${job.service?.name || 'No Service'}`;
  };
  
  // Check if day is a weekend
  const isWeekend = (day) => {
    const dayOfWeek = day.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const { start, end } = getDateRange();
  const days = eachDayOfInterval({ start, end });

  // Weekly view columns
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div data-testid="calendar-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            {isAuditor ? 'My Calendar' : 'Calendar'}
          </h1>
          <p className="text-muted-foreground">
            {viewMode === 'week' 
              ? `Week of ${format(start, 'd MMM')} - ${format(end, 'd MMM yyyy')}`
              : format(currentDate, 'MMMM yyyy')}
          </p>
        </div>
        
        {/* View mode toggle */}
        <div className="flex items-center gap-2">
          {(isAdmin || isScheduler) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNotificationsDialogOpen(true)}
              data-testid="open-calendar-notifications-btn"
            >
              <Bell className="h-4 w-4 mr-1" />
              Notifications
            </Button>
          )}
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('week')}
            data-testid="view-week-btn"
          >
            <CalendarDays className="h-4 w-4 mr-1" />
            Week
          </Button>
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('month')}
            data-testid="view-month-btn"
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            Month
          </Button>
        </div>
      </div>

      <CalendarNotificationsDialog open={notificationsDialogOpen} onOpenChange={setNotificationsDialogOpen} />

      {/* Filters */}
      {!isAuditor && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <Select value={clientFilter || "all"} onValueChange={(v) => setClientFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="calendar-client-filter">
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: client.colour || '#6B7280' }}
                        />
                        {client.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={siteFilter || "all"} onValueChange={(v) => setSiteFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="calendar-site-filter">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id.toString()}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={auditorFilter || "all"} onValueChange={(v) => setAuditorFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="calendar-auditor-filter">
                  <SelectValue placeholder="All Auditors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Auditors</SelectItem>
                  {auditors.map((auditor) => (
                    <SelectItem key={auditor.id} value={auditor.id.toString()}>
                      {auditor.first_name} {auditor.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px]" data-testid="calendar-status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <RegionFilter value={regionId} onChange={setRegionId} />
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                  <Filter className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate('prev')} data-testid="nav-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => navigate('next')} data-testid="nav-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="nav-today">
            Today
          </Button>
        </div>
        
        {/* Legend */}
        <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground">
          <span>Status:</span>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span>Scheduled</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span>Cancelled</span>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : viewMode === 'week' ? (
            /* Weekly View */
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Header row */}
                <div className="grid grid-cols-7 border-b">
                  {days.map((day, idx) => {
                    const bankHoliday = getBankHolidayForDay(day);
                    const weekend = isWeekend(day);
                    return (
                      <div 
                        key={idx} 
                        className={`p-3 text-center border-r last:border-r-0 ${
                          isToday(day) ? 'bg-blue-50' : ''
                        } ${bankHoliday ? 'bg-amber-100 border-amber-200' : weekend ? 'bg-slate-200/70' : ''}`}
                      >
                        <div className={`text-xs uppercase ${weekend ? 'text-slate-500 font-medium' : 'text-muted-foreground'}`}>
                          {weekDays[idx]}
                        </div>
                        <div className={`text-lg font-semibold ${
                          isToday(day) ? 'text-blue-600' : weekend ? 'text-slate-500' : ''
                        }`}>
                          {format(day, 'd')}
                        </div>
                        {bankHoliday && (
                          <div className="text-[10px] text-amber-800 font-semibold truncate" title={bankHoliday.name}>
                            {bankHoliday.name}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Jobs row */}
                <div className="grid grid-cols-7 min-h-[400px]">
                  {days.map((day, idx) => {
                    const dayJobs = getJobsForDay(day);
                    const bankHoliday = getBankHolidayForDay(day);
                    const weekend = isWeekend(day);
                    return (
                      <div 
                        key={idx} 
                        className={`border-r last:border-r-0 p-2 ${
                          isToday(day) ? 'bg-blue-50/50' : ''
                        } ${bankHoliday ? 'bg-amber-50/50' : weekend ? 'bg-slate-100' : ''}`}
                      >
                        <div className="space-y-1">
                          {dayJobs.map((job) => (
                            <div
                              key={job.id}
                              className={`rounded-md p-2 text-xs cursor-default hover:opacity-80 transition-opacity${job.is_locked ? ' ring-[3px] ring-amber-500' : ''}`}
                              style={{ 
                                backgroundColor: `${getJobColor(job)}20`,
                                borderLeft: `3px solid ${getJobColor(job)}`
                              }}
                              title={`${getJobTooltip(job)}${job.is_locked ? ' — Locked' : ''}`}
                              data-testid={job.is_locked ? `calendar-locked-job-${job.id}` : `calendar-job-${job.id}`}
                            >
                              {job.event_type?.startsWith('LEAVE') ? (
                                // Leave / Event display
                                (() => {
                                  const { emoji, label, detail } = getNonJobLabel(job);
                                  return (
                                    <>
                                      <div className="flex items-center gap-1 mb-1">
                                        {job.is_locked && <Lock className="h-2.5 w-2.5 text-amber-700" aria-label="Locked" />}
                                        <span className="font-medium truncate">{emoji} {label}</span>
                                      </div>
                                      {detail && job.event_type !== 'LEAVE_OTHER' && (
                                        <div className="text-muted-foreground truncate italic">
                                          {detail}
                                        </div>
                                      )}
                                      {job.auditors?.length > 0 && (
                                        <div className="text-muted-foreground truncate">
                                          {job.auditors.map(a => `${a.first_name} ${a.last_name}`).join(', ')}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()
                              ) : (
                                // Client job display
                                <>
                                  <div className="flex items-center gap-1 mb-1">
                                    <div className={`w-1.5 h-1.5 rounded-full ${statusColors[job.status]}`} />
                                    {job.is_locked && <Lock className="h-2.5 w-2.5 text-amber-700" aria-label="Locked" />}
                                    <span className="font-medium truncate">{job.site?.name || 'No Site'}</span>
                                  </div>
                                  <div className="text-muted-foreground truncate">
                                    {job.service?.name}
                                  </div>
                                  {job.auditors?.length > 0 && (
                                    <div className="text-muted-foreground truncate">
                                      {job.auditors.map(a => a.first_name).join(', ')}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                          {dayJobs.length === 0 && (
                            <div className="text-xs text-muted-foreground text-center py-4">
                              —
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            /* Monthly View */
            <div>
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b">
                {weekDays.map((day) => (
                  <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground uppercase border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {/* Add empty cells for days before the start of month */}
                {Array.from({ length: (start.getDay() + 6) % 7 }).map((_, idx) => (
                  <div key={`empty-start-${idx}`} className="min-h-[100px] bg-slate-100 border-r border-b" />
                ))}
                
                {days.map((day, idx) => {
                  const dayJobs = getJobsForDay(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const bankHoliday = getBankHolidayForDay(day);
                  const weekend = isWeekend(day);
                  
                  return (
                    <div 
                      key={idx}
                      className={`min-h-[100px] border-r border-b last:border-r-0 p-1 ${
                        !isCurrentMonth ? 'bg-slate-100' : ''
                      } ${isToday(day) ? 'bg-blue-50' : ''} ${
                        bankHoliday ? 'bg-amber-50' : weekend && isCurrentMonth ? 'bg-slate-100' : ''
                      }`}
                    >
                      <div className={`text-sm font-medium mb-1 px-1 ${
                        isToday(day) ? 'text-blue-600' : ''
                      } ${!isCurrentMonth ? 'text-muted-foreground' : ''} ${
                        weekend && isCurrentMonth ? 'text-slate-500' : ''
                      }`}>
                        {format(day, 'd')}
                        {bankHoliday && (
                          <span className="ml-1 text-[9px] text-amber-700 font-semibold" title={bankHoliday.name}>
                            ({bankHoliday.name.substring(0, 8)}...)
                          </span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {(() => {
                          // iter341 — pin locked jobs to the visible top-3 so
                          // their amber/padlock visual is never hidden behind
                          // the "+N more" chip. Stable order is preserved
                          // within each lock group.
                          const sorted = [...dayJobs].sort((a, b) => {
                            if ((a.is_locked ? 1 : 0) !== (b.is_locked ? 1 : 0)) {
                              return a.is_locked ? -1 : 1;
                            }
                            return 0;
                          });
                          const visible = sorted.slice(0, 3);
                          const overflow = sorted.slice(3);
                          const overflowHasLocked = overflow.some(j => j.is_locked);
                          return (
                            <>
                              {visible.map((job) => (
                                <div
                                  key={job.id}
                                  className={`rounded px-1.5 py-0.5 text-[10px] truncate cursor-default${job.is_locked ? ' ring-[3px] ring-amber-500' : ''}`}
                                  style={{
                                    backgroundColor: `${getJobColor(job)}30`,
                                    color: getJobColor(job)
                                  }}
                                  title={`${getJobTooltip(job)}${job.is_locked ? ' — Locked' : ''}`}
                                  data-testid={job.is_locked ? `calendar-locked-job-${job.id}` : `calendar-job-${job.id}`}
                                >
                                  <div className="flex items-center gap-1">
                                    <div className={`w-1 h-1 rounded-full flex-shrink-0 ${statusColors[job.status]}`} />
                                    {job.is_locked && <Lock className="h-2.5 w-2.5 flex-shrink-0 text-amber-700" aria-label="Locked" />}
                                    <span className="truncate font-medium">{getJobDisplayName(job)}</span>
                                  </div>
                                </div>
                              ))}
                              {overflow.length > 0 && (
                                <div
                                  className={`text-[10px] px-1 flex items-center gap-1 ${overflowHasLocked ? 'text-amber-800 font-medium' : 'text-muted-foreground'}`}
                                  title={overflowHasLocked
                                    ? `+${overflow.length} more (${overflow.filter(j => j.is_locked).length} locked)`
                                    : `+${overflow.length} more`}
                                  data-testid={overflowHasLocked ? `calendar-overflow-with-locked-${format(day, 'yyyy-MM-dd')}` : undefined}
                                >
                                  {overflowHasLocked && <Lock className="h-2.5 w-2.5 text-amber-700" aria-label="Overflow contains locked jobs" />}
                                  +{overflow.length} more
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
                
                {/* Add empty cells for days after the end of month */}
                {Array.from({ length: (7 - ((end.getDay() + 6) % 7) - 1) % 7 }).map((_, idx) => (
                  <div key={`empty-end-${idx}`} className="min-h-[100px] bg-slate-100 border-r border-b last:border-r-0" />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="mt-4 text-sm text-muted-foreground text-center">
        {(() => {
          const clientJobs = jobs.filter(j => !j.event_type?.startsWith('LEAVE'));
          const leaveEvents = jobs.filter(j => j.event_type?.startsWith('LEAVE'));
          const period = viewMode === 'week' ? 'week' : 'month';
          
          if (clientJobs.length === 0 && leaveEvents.length === 0) {
            return `No entries in this ${period}`;
          }
          
          const parts = [];
          if (clientJobs.length > 0) {
            parts.push(`${clientJobs.length} job${clientJobs.length !== 1 ? 's' : ''}`);
          }
          if (leaveEvents.length > 0) {
            parts.push(`${leaveEvents.length} leave/holiday${leaveEvents.length !== 1 ? 's' : ''}`);
          }
          
          return `${parts.join(', ')} in this ${period}${hasActiveFilters ? ' (filtered)' : ''}`;
        })()}
      </div>

      {/* iter341 — lock legend */}
      <div className="mt-3">
        <LockLegend />
      </div>
    </div>
  );
}
